import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import {
  activeCurtainImpacts,
  appendCurtainImpact,
  createCurtainParticleData,
  curtainProfileForWidth,
  curtainSuccessDelay,
  normalizeCurtainPointer,
  pointerInCurtainBounds,
  CURTAIN_ATTRIBUTE_SIZE,
  CURTAIN_IMPACT_DURATION_MS,
  CURTAIN_SUCCESS_HARD_LIMIT_MS,
  type CurtainBounds,
  type CurtainImpact,
} from "@/lib/auth-particle-curtain";

export interface AuthParticleCurtainHandle {
  playSuccessTransition: () => Promise<void>;
}

const CONTEXT_OPTIONS: WebGLContextAttributes = {
  alpha: true,
  antialias: false,
  depth: false,
  powerPreference: "high-performance",
  premultipliedAlpha: true,
};

const VERTEX_SOURCE = `#version 300 es
precision highp float;

in vec3 aBase;
in float aSeed;
in float aSize;
in float aTone;

uniform float uTime;
uniform float uDpr;
uniform vec2 uPointer;
uniform float uPointerActive;
uniform float uPointerSpeed;
uniform vec4 uImpacts[3];
uniform float uSuccess;

out vec4 vColor;

float cubicOut(float value) {
  float inverse = 1.0 - value;
  return 1.0 - inverse * inverse * inverse;
}

void main() {
  float seconds = uTime * 0.001;
  vec3 position = aBase;

  position.x += sin(position.y * 2.45 + seconds * 0.29 + aSeed * 2.1) * 0.035;
  position.y += cos(position.x * 2.05 - seconds * 0.23 + aSeed) * 0.026;
  position.z += sin(position.y * 3.2 + seconds * 0.18) * 0.055;
  position.z += cos((position.x - position.y) * 2.8 - seconds * 0.31) * 0.032;

  float signalPhase = seconds * 0.2 + aSeed * 8.0;
  position.xy += aTone * vec2(sin(signalPhase), cos(signalPhase * 0.81)) * 0.017;

  float perspective = 1.0 / max(0.72, 1.08 - position.z * 0.31);
  vec2 projected = vec2(
    (position.x * 0.9 - 0.035) * perspective,
    position.y * 0.9 * perspective
  );

  vec2 pointerDelta = projected - uPointer;
  float pointerAngle = atan(pointerDelta.y, pointerDelta.x);
  vec2 warpedPointerDelta = vec2(
    pointerDelta.x * (0.9 + aSeed * 0.13) + pointerDelta.y * sin(aSeed * 17.0) * 0.075,
    pointerDelta.y * (1.06 - aSeed * 0.1)
  );
  float pointerDistance = length(warpedPointerDelta)
    + sin(pointerAngle * 5.0 + aSeed * 13.0 + position.z * 4.0) * 0.012;
  vec2 pointerDirection = pointerDelta / max(length(pointerDelta), 0.001);
  float pointerRadius = mix(0.14, 0.24, uPointerSpeed);
  float pointerForce = smoothstep(pointerRadius, 0.0, pointerDistance) * uPointerActive;
  float pointerTexture = 0.82 + 0.18 * sin(aSeed * 31.0 + position.z * 7.0);
  projected += pointerDirection * pointerForce * pointerTexture * mix(0.045, 0.105, uPointerSpeed);
  position.z -= pointerForce * pointerTexture * mix(0.27, 0.5, uPointerSpeed);

  for (int index = 0; index < 3; index += 1) {
    vec4 impact = uImpacts[index];
    vec2 impactDelta = projected - impact.xy;
    float impactAngle = atan(impactDelta.y, impactDelta.x);
    vec2 warpedImpactDelta = vec2(
      impactDelta.x * (0.9 + aSeed * 0.12),
      impactDelta.y * (1.08 - aSeed * 0.08)
    );
    float distanceToImpact = length(warpedImpactDelta)
      + sin(impactAngle * 6.0 + aSeed * 17.0 + position.z * 5.0) * 0.015;
    float radius = impact.z * 0.84;
    float decay = max(0.0, 1.0 - impact.z);
    float reach = smoothstep(radius + 0.32, max(0.0, radius - 0.2), distanceToImpact);
    float wave = 0.67 + 0.33 * sin(
      distanceToImpact * 8.5 - impact.z * 6.0 + aSeed * 0.55
    );
    vec2 direction = impactDelta / max(length(impactDelta), 0.001);
    float force = reach * wave * decay * impact.w;
    projected += direction * force * 0.09;
    position.z += force * 0.24;
  }

  float success = cubicOut(clamp(uSuccess, 0.0, 1.0));
  projected = mix(projected, vec2(-0.22, 0.04), success);

  float depth = clamp((position.z + 0.68) / 1.36, 0.0, 1.0);
  vec3 graphite = mix(vec3(0.5, 0.48, 0.45), vec3(0.12, 0.112, 0.102), depth);
  vec3 terracotta = vec3(0.71, 0.37, 0.265);
  float signalPulse = aTone * (0.07 + 0.09 * sin(seconds * 0.68 + aSeed * 11.0));
  float brandVertical = smoothstep(0.7, 0.82, projected.y);
  float brandHorizontal = 1.0 - smoothstep(-0.56, -0.43, projected.x);
  float brandQuietZone = 1.0 - brandVertical * brandHorizontal;
  float alpha = (mix(0.62, 0.98, depth) + signalPulse)
    * brandQuietZone
    * (1.0 - success);

  gl_Position = vec4(projected, 0.0, 1.0);
  gl_PointSize = max(
    1.05 * uDpr,
    aSize * mix(1.75, 4.75, depth) * uDpr * (1.0 - success * 0.52)
  );
  vColor = vec4(mix(graphite, terracotta, aTone), alpha);
}`;

const FRAGMENT_SOURCE = `#version 300 es
precision highp float;

in vec4 vColor;
out vec4 outputColor;

void main() {
  float radius = length(gl_PointCoord - vec2(0.5));
  float antialias = fwidth(radius);
  float coverage = 1.0 - smoothstep(0.5 - antialias, 0.5, radius);
  if (coverage <= 0.0) discard;
  outputColor = vec4(vColor.rgb, vColor.a * coverage);
}`;

interface UniformLocations {
  time: WebGLUniformLocation | null;
  dpr: WebGLUniformLocation | null;
  pointer: WebGLUniformLocation | null;
  pointerActive: WebGLUniformLocation | null;
  pointerSpeed: WebGLUniformLocation | null;
  impacts: WebGLUniformLocation | null;
  success: WebGLUniformLocation | null;
}

interface RenderResources {
  program: WebGLProgram;
  buffer: WebGLBuffer;
  vao: WebGLVertexArrayObject;
  pointCount: number;
  uniforms: UniformLocations;
}

function compileShader(gl: WebGL2RenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("Unable to create particle shader");
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) ?? "Particle shader compilation failed";
    gl.deleteShader(shader);
    throw new Error(message);
  }
  return shader;
}

function createRenderResources(
  gl: WebGL2RenderingContext,
  particleData: Float32Array,
): RenderResources {
  const vertex = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SOURCE);
  const fragment = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SOURCE);
  const program = gl.createProgram();
  if (!program) {
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);
    throw new Error("Unable to create particle program");
  }
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const message = gl.getProgramInfoLog(program) ?? "Particle program linking failed";
    gl.deleteProgram(program);
    throw new Error(message);
  }

  const buffer = gl.createBuffer();
  const vao = gl.createVertexArray();
  if (!buffer || !vao) {
    if (buffer) gl.deleteBuffer(buffer);
    if (vao) gl.deleteVertexArray(vao);
    gl.deleteProgram(program);
    throw new Error("Unable to allocate particle buffers");
  }

  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, particleData, gl.STATIC_DRAW);
  const stride = CURTAIN_ATTRIBUTE_SIZE * Float32Array.BYTES_PER_ELEMENT;
  const attributes = [
    { name: "aBase", size: 3, offset: 0 },
    { name: "aSeed", size: 1, offset: 3 },
    { name: "aSize", size: 1, offset: 4 },
    { name: "aTone", size: 1, offset: 5 },
  ];
  for (const attribute of attributes) {
    const location = gl.getAttribLocation(program, attribute.name);
    if (location < 0) continue;
    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(
      location,
      attribute.size,
      gl.FLOAT,
      false,
      stride,
      attribute.offset * Float32Array.BYTES_PER_ELEMENT,
    );
  }
  gl.bindVertexArray(null);

  return {
    program,
    buffer,
    vao,
    pointCount: particleData.length / CURTAIN_ATTRIBUTE_SIZE,
    uniforms: {
      time: gl.getUniformLocation(program, "uTime"),
      dpr: gl.getUniformLocation(program, "uDpr"),
      pointer: gl.getUniformLocation(program, "uPointer"),
      pointerActive: gl.getUniformLocation(program, "uPointerActive"),
      pointerSpeed: gl.getUniformLocation(program, "uPointerSpeed"),
      impacts: gl.getUniformLocation(program, "uImpacts[0]"),
      success: gl.getUniformLocation(program, "uSuccess"),
    },
  };
}

function destroyRenderResources(
  gl: WebGL2RenderingContext,
  resources: RenderResources | null,
) {
  if (!resources) return;
  gl.deleteBuffer(resources.buffer);
  gl.deleteVertexArray(resources.vao);
  gl.deleteProgram(resources.program);
}

function drawStaticFallback(
  canvas: HTMLCanvasElement,
  particleData: Float32Array,
  dpr: number,
) {
  const context = canvas.getContext("2d");
  if (!context) return;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  canvas.width = Math.max(1, Math.round(width * dpr));
  canvas.height = Math.max(1, Math.round(height * dpr));
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  context.clearRect(0, 0, width, height);

  for (let offset = 0; offset < particleData.length; offset += CURTAIN_ATTRIBUTE_SIZE) {
    const baseX = particleData[offset] ?? 0;
    const baseY = particleData[offset + 1] ?? 0;
    const baseZ = particleData[offset + 2] ?? 0;
    const size = particleData[offset + 4] ?? 1;
    const tone = particleData[offset + 5] ?? 0;
    const perspective = 1 / Math.max(0.72, 1.08 - baseZ * 0.31);
    const x = width * (0.5 + (baseX * 0.9 - 0.035) * perspective * 0.5);
    const y = height * (0.5 - baseY * 0.9 * perspective * 0.5);
    const depth = Math.min(1, Math.max(0, (baseZ + 0.68) / 1.36));
    const shade = Math.round(176 - depth * 143);
    const alpha = 0.31 + depth * 0.67;
    context.fillStyle = tone > 0.5
      ? "rgba(181,94,67,.76)"
      : `rgba(${shade},${Math.max(30, shade - 5)},${Math.max(27, shade - 10)},${alpha})`;
    context.beginPath();
    context.arc(x, y, Math.max(0.45, size * (0.65 + depth * 0.8)), 0, Math.PI * 2);
    context.fill();
  }
}

export const AuthParticleCurtain = forwardRef<AuthParticleCurtainHandle>(
  function AuthParticleCurtain(_, forwardedRef) {
    const hostRef = useRef<HTMLDivElement>(null);
    const webglCanvasRef = useRef<HTMLCanvasElement>(null);
    const fallbackCanvasRef = useRef<HTMLCanvasElement>(null);
    const successTransitionRef = useRef<() => Promise<void>>(() => Promise.resolve());

    useImperativeHandle(forwardedRef, () => ({
      playSuccessTransition: () => successTransitionRef.current(),
    }), []);

    useEffect(() => {
      const host = hostRef.current;
      const webglCanvas = webglCanvasRef.current;
      const fallbackCanvas = fallbackCanvasRef.current;
      const interactionRoot = host?.parentElement;
      if (!host || !webglCanvas || !fallbackCanvas || !interactionRoot) return;

      const reducedMotionMedia = window.matchMedia("(prefers-reduced-motion: reduce)");
      let reducedMotion = reducedMotionMedia.matches;
      let profile = curtainProfileForWidth(window.innerWidth);
      let particleData = createCurtainParticleData(profile.particleCount);
      let gl = webglCanvas.getContext("webgl2", CONTEXT_OPTIONS);
      let resources: RenderResources | null = null;
      let bounds: CurtainBounds = { left: 0, top: 0, width: 0, height: 0 };
      let frameRequest = 0;
      let lastRenderedAt = 0;
      let sampleStartedAt = performance.now();
      let sampleFrames = 0;
      let contextLost = false;
      let disposed = false;
      let successStartedAt: number | null = null;
      let successTimer = 0;
      let successResolve: (() => void) | null = null;
      let impacts: CurtainImpact[] = [];
      const impactUniforms = new Float32Array(12);
      const pointer = {
        x: -2,
        y: -2,
        targetX: -2,
        targetY: -2,
        active: 0,
        targetActive: 0,
        speed: 0,
        targetSpeed: 0,
        lastClientX: 0,
        lastClientY: 0,
        lastAt: 0,
        sampled: false,
      };

      const setRenderer = (renderer: "webgl2" | "canvas2d") => {
        host.dataset.renderer = renderer;
        webglCanvas.hidden = renderer !== "webgl2";
        fallbackCanvas.hidden = renderer !== "canvas2d";
      };

      const renderFallback = () => {
        setRenderer("canvas2d");
        drawStaticFallback(
          fallbackCanvas,
          particleData,
          Math.min(window.devicePixelRatio || 1, profile.dprCap),
        );
      };

      const initializeWebgl = () => {
        if (!gl) {
          renderFallback();
          return false;
        }
        destroyRenderResources(gl, resources);
        resources = createRenderResources(gl, particleData);
        gl.clearColor(0, 0, 0, 0);
        gl.disable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        setRenderer("webgl2");
        return true;
      };

      const updateProfile = () => {
        const next = curtainProfileForWidth(window.innerWidth);
        if (next.particleCount !== profile.particleCount) {
          profile = next;
          particleData = createCurtainParticleData(profile.particleCount);
          if (gl && resources && !contextLost) initializeWebgl();
        } else {
          profile = next;
        }
        host.dataset.particleCount = String(profile.particleCount);
        host.dataset.targetFps = String(profile.targetFps);
      };

      const resize = () => {
        const rectangle = host.getBoundingClientRect();
        bounds = {
          left: rectangle.left,
          top: rectangle.top,
          width: rectangle.width,
          height: rectangle.height,
        };
        updateProfile();
        const dpr = Math.min(window.devicePixelRatio || 1, profile.dprCap);
        const width = Math.max(1, Math.round(rectangle.width * dpr));
        const height = Math.max(1, Math.round(rectangle.height * dpr));
        if (webglCanvas.width !== width) webglCanvas.width = width;
        if (webglCanvas.height !== height) webglCanvas.height = height;
        if (gl && !contextLost) gl.viewport(0, 0, width, height);
        if (!gl || contextLost) renderFallback();
      };

      const draw = (now: number) => {
        if (!gl || !resources || contextLost) return;
        const dpr = Math.min(window.devicePixelRatio || 1, profile.dprCap);
        pointer.x += (pointer.targetX - pointer.x) * 0.16;
        pointer.y += (pointer.targetY - pointer.y) * 0.16;
        pointer.active += (pointer.targetActive - pointer.active) * 0.14;
        pointer.speed += (pointer.targetSpeed - pointer.speed) * 0.18;
        pointer.targetSpeed *= 0.91;
        impacts = activeCurtainImpacts(impacts, now);
        impactUniforms.fill(0);
        impacts.forEach((impact, index) => {
          const offset = index * 4;
          impactUniforms[offset] = impact.x;
          impactUniforms[offset + 1] = impact.y;
          impactUniforms[offset + 2] = Math.min(
            1,
            (now - impact.startedAt) / CURTAIN_IMPACT_DURATION_MS,
          );
          impactUniforms[offset + 3] = impact.strength;
        });
        const success = successStartedAt === null
          ? 0
          : Math.min(1, (now - successStartedAt) / curtainSuccessDelay(false));

        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.useProgram(resources.program);
        gl.bindVertexArray(resources.vao);
        gl.uniform1f(resources.uniforms.time, reducedMotion ? 0 : now);
        gl.uniform1f(resources.uniforms.dpr, dpr);
        gl.uniform2f(resources.uniforms.pointer, pointer.x, pointer.y);
        gl.uniform1f(resources.uniforms.pointerActive, reducedMotion ? 0 : pointer.active);
        gl.uniform1f(resources.uniforms.pointerSpeed, reducedMotion ? 0 : pointer.speed);
        gl.uniform4fv(resources.uniforms.impacts, impactUniforms);
        gl.uniform1f(resources.uniforms.success, reducedMotion ? 0 : success);
        gl.drawArrays(gl.POINTS, 0, resources.pointCount);
        gl.bindVertexArray(null);
      };

      const animate = (now: number) => {
        frameRequest = 0;
        if (disposed || document.hidden || reducedMotion || contextLost || !gl) return;
        const interval = 1_000 / profile.targetFps;
        const elapsed = now - lastRenderedAt;
        if (elapsed >= interval) {
          draw(now);
          lastRenderedAt = now - (elapsed % interval);
          sampleFrames += 1;
          if (now - sampleStartedAt >= 1_000) {
            host.dataset.measuredFps = String(Math.round(
              sampleFrames * 1_000 / (now - sampleStartedAt),
            ));
            sampleFrames = 0;
            sampleStartedAt = now;
          }
        }
        frameRequest = window.requestAnimationFrame(animate);
      };

      const startAnimation = () => {
        if (frameRequest || disposed || document.hidden || reducedMotion || contextLost || !gl) return;
        frameRequest = window.requestAnimationFrame(animate);
      };

      const stopAnimation = () => {
        if (frameRequest) window.cancelAnimationFrame(frameRequest);
        frameRequest = 0;
      };

      const handlePointerMove = (event: PointerEvent) => {
        if (reducedMotion || !pointerInCurtainBounds(event.clientX, event.clientY, bounds)) {
          pointer.targetActive = 0;
          pointer.targetSpeed = 0;
          return;
        }
        const normalized = normalizeCurtainPointer(event.clientX, event.clientY, bounds);
        const now = performance.now();
        const elapsed = pointer.sampled ? Math.max(8, now - pointer.lastAt) : 16;
        const distance = pointer.sampled
          ? Math.hypot(event.clientX - pointer.lastClientX, event.clientY - pointer.lastClientY)
          : 0;
        pointer.targetX = normalized.x;
        pointer.targetY = normalized.y;
        pointer.targetActive = 1;
        pointer.targetSpeed = Math.min(1, distance / elapsed / 1.15);
        pointer.lastClientX = event.clientX;
        pointer.lastClientY = event.clientY;
        pointer.lastAt = now;
        pointer.sampled = true;
      };

      const handlePointerDown = (event: PointerEvent) => {
        if (reducedMotion || !pointerInCurtainBounds(event.clientX, event.clientY, bounds)) return;
        impacts = appendCurtainImpact(impacts, {
          ...normalizeCurtainPointer(event.clientX, event.clientY, bounds),
          startedAt: performance.now(),
          strength: event.pointerType === "touch" ? 1 : 0.86,
        });
      };

      const handlePointerEnd = (event: PointerEvent) => {
        if (event.pointerType !== "touch") return;
        pointer.targetActive = 0;
        pointer.targetSpeed = 0;
      };

      const handleVisibilityChange = () => {
        if (document.hidden) {
          stopAnimation();
          return;
        }
        sampleStartedAt = performance.now();
        sampleFrames = 0;
        if (reducedMotion) draw(0);
        else startAnimation();
      };

      const handleReducedMotion = (event: MediaQueryListEvent) => {
        reducedMotion = event.matches;
        host.dataset.reducedMotion = String(reducedMotion);
        pointer.targetActive = 0;
        impacts = [];
        if (reducedMotion) {
          stopAnimation();
          draw(0);
        } else {
          startAnimation();
        }
      };

      const handleContextLost = (event: Event) => {
        event.preventDefault();
        contextLost = true;
        stopAnimation();
        renderFallback();
      };

      const handleContextRestored = () => {
        contextLost = false;
        gl = webglCanvas.getContext("webgl2", CONTEXT_OPTIONS);
        if (!initializeWebgl()) return;
        resize();
        if (reducedMotion) draw(0);
        else startAnimation();
      };

      const finishSuccessTransition = () => {
        window.clearTimeout(successTimer);
        successTimer = 0;
        const resolve = successResolve;
        successResolve = null;
        resolve?.();
      };

      successTransitionRef.current = () => {
        if (reducedMotion || !gl || contextLost) return Promise.resolve();
        finishSuccessTransition();
        successStartedAt = performance.now();
        startAnimation();
        return new Promise<void>((resolve) => {
          successResolve = resolve;
          successTimer = window.setTimeout(
            finishSuccessTransition,
            Math.min(curtainSuccessDelay(false), CURTAIN_SUCCESS_HARD_LIMIT_MS),
          );
        });
      };

      const resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(host);
      interactionRoot.addEventListener("pointermove", handlePointerMove, { passive: true });
      interactionRoot.addEventListener("pointerdown", handlePointerDown, { passive: true });
      interactionRoot.addEventListener("pointerup", handlePointerEnd, { passive: true });
      interactionRoot.addEventListener("pointercancel", handlePointerEnd, { passive: true });
      document.addEventListener("visibilitychange", handleVisibilityChange);
      reducedMotionMedia.addEventListener("change", handleReducedMotion);
      webglCanvas.addEventListener("webglcontextlost", handleContextLost);
      webglCanvas.addEventListener("webglcontextrestored", handleContextRestored);

      host.dataset.reducedMotion = String(reducedMotion);
      updateProfile();
      resize();
      try {
        if (initializeWebgl()) {
          resize();
          if (reducedMotion) draw(0);
          else startAnimation();
        }
      } catch {
        if (gl) destroyRenderResources(gl, resources);
        resources = null;
        gl = null;
        renderFallback();
      }

      return () => {
        disposed = true;
        successTransitionRef.current = () => Promise.resolve();
        finishSuccessTransition();
        stopAnimation();
        resizeObserver.disconnect();
        interactionRoot.removeEventListener("pointermove", handlePointerMove);
        interactionRoot.removeEventListener("pointerdown", handlePointerDown);
        interactionRoot.removeEventListener("pointerup", handlePointerEnd);
        interactionRoot.removeEventListener("pointercancel", handlePointerEnd);
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        reducedMotionMedia.removeEventListener("change", handleReducedMotion);
        webglCanvas.removeEventListener("webglcontextlost", handleContextLost);
        webglCanvas.removeEventListener("webglcontextrestored", handleContextRestored);
        if (gl) destroyRenderResources(gl, resources);
      };
    }, []);

    return (
      <div className="auth-curtain" ref={hostRef} aria-hidden="true">
        <canvas className="auth-curtain__canvas" ref={webglCanvasRef} />
        <canvas className="auth-curtain__canvas" ref={fallbackCanvasRef} hidden />
      </div>
    );
  },
);
